<template>
   <div class="app-version-alert" v-if="isNewVersionAvailable">
      <p>There is a newer version of the app available.</p>
      <div class="alert-actions">
         <Btn
            @handle-action="reloadPage"
            buttonType="small"
            text="Refresh Now"
            action
         />
      </div>
   </div>
</template>
<script setup lang="ts">
const $config = useRuntimeConfig();
const isNewVersionAvailable = ref(false);
const currentClientVersion = ref("");
const latestServerVersion = ref("");

const reloadPage = () => {
   if (import.meta.client) {
      localStorage.removeItem("app-version");
      location.reload();
   }
};

const checkForNewVersion = async () => {
   if (!import.meta.client) return;

   try {
      // Fetch the latest version from your API or a version endpoint
      const response = await $fetch<{ version: string }>("/api/version");
      latestServerVersion.value = response.version;

      // If this is the first time checking (no stored version), just store it
      if (!localStorage.getItem("app-version")) {
         currentClientVersion.value = latestServerVersion.value;
         localStorage.setItem("app-version", latestServerVersion.value);
         return; // Don't show alert on first load
      }

      // Compare with stored client version - only show alert if versions differ
      if (currentClientVersion.value !== latestServerVersion.value) {
         // console.log("New version detected:", {
         //    current: currentClientVersion.value,
         //    latest: latestServerVersion.value,
         // });
         isNewVersionAvailable.value = true;
         // Don't update currentClientVersion here - wait for user action
      }
   } catch (error) {
      console.warn("Failed to check for app version updates:", error);
   }
};
let versionCheckInterval: string | number | NodeJS.Timeout | undefined;
onMounted(() => {
   if (import.meta.client) {
      // Get the stored version from localStorage
      const storedVersion = localStorage.getItem("app-version");
      currentClientVersion.value = storedVersion || $config.public.buildVersion;

      // Check for new version immediately
      checkForNewVersion();

      // Set up periodic checking every 5 minutes
      versionCheckInterval = setInterval(checkForNewVersion, 5 * 60 * 1000);
   }
});
onBeforeUnmount(() => {
   clearInterval(versionCheckInterval);
});
</script>
<style lang="scss">
div.app-version-alert {
   position: fixed;
   @include width(2, 2);
   min-width: 15rem;
   bottom: 2rem;
   right: 2rem; // Changed from left to right for better visibility
   background-color: $white;
   border: 1px solid $border-lt;
   border-radius: $tooltip-rad;
   padding: 1rem;
   z-index: map-get($zindex, ALERT);
   box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
   max-width: 300px;

   .alert-actions {
      display: flex;
      gap: 0.5rem;
      margin-top: 1rem;
      justify-content: flex-end;
   }

   @media print {
      display: none;
   }
}
</style>
